import { Section, DarkSection, Question, CheckboxGroup, PriorityRow, InfoTip } from "./PortalShared";

export default function PortalPhase1({ formData, updateField, toggleCheckbox }) {
  return (
    <>
      {/* === 01 Überblick === */}
      <Section num="01" title="Überblick">
        <Question label="Primäres Ziel" text="Was soll die Homepage konkret erreichen?">
          <textarea className="portal-input portal-textarea tall" value={formData.primary_goal || ""}
            onChange={e => updateField("primary_goal", e.target.value)}
            placeholder="z.B. Qualifizierte Leads für Enterprise-Beratungsmandate generieren …"
            data-testid="field-primary_goal" />
        </Question>

        <Question label="Primäre Conversion" text="Welche eine Aktion soll der Besucher ausführen?"
          info="Eine Conversion ist die gewünschte Handlung eines Besuchers auf Ihrer Website — z.B. ein Formular absenden oder einen Termin buchen.">
          <CheckboxGroup fieldKey="primary_conversion"
            options={["Termin buchen", "Anfrage senden", "Download", "Kauf", "Newsletter", "Anderes"]}
            otherKey="primary_conversion_other" formData={formData} toggleCheckbox={toggleCheckbox} updateField={updateField} />
        </Question>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
          <Question label="Zielgruppe" text="Wer soll angesprochen werden?">
            <input type="text" className="portal-input" value={formData.target_audience || ""}
              onChange={e => updateField("target_audience", e.target.value)}
              placeholder="z.B. IT-Entscheider, KMU Schweiz" data-testid="field-target_audience" />
          </Question>
          <Question label="Go-live Datum">
            <input type="date" className="portal-input" value={formData.go_live_date || ""}
              onChange={e => updateField("go_live_date", e.target.value)} data-testid="field-go_live_date" />
          </Question>
        </div>

        <Question label="Traffic-Quellen" text="Woher kommt der Traffic auf die Seite?"
          info="Über welche Kanäle gelangen Besucher auf Ihre Website? Dies beeinflusst die Seitenstruktur und das Messaging.">
          <CheckboxGroup fieldKey="traffic_sources"
            options={["Google Ads", "LinkedIn Ads", "Meta Ads", "SEO / Organisch", "Newsletter", "Partner", "Direkt"]}
            formData={formData} toggleCheckbox={toggleCheckbox} updateField={updateField} />
        </Question>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Question label="Ansprechpartner (fachlich)">
            <input type="text" className="portal-input mb-1.5" value={formData.contact_name || ""}
              onChange={e => updateField("contact_name", e.target.value)}
              placeholder="Name, Funktion" data-testid="field-contact_name" />
            <input type="email" className="portal-input" value={formData.contact_email || ""}
              onChange={e => updateField("contact_email", e.target.value)}
              placeholder="E-Mail" data-testid="field-contact_email" />
          </Question>
          <Question label="Finaler Entscheider">
            <input type="text" className="portal-input" value={formData.decision_maker || ""}
              onChange={e => updateField("decision_maker", e.target.value)}
              placeholder="Name, Funktion" data-testid="field-decision_maker" />
          </Question>
        </div>
      </Section>

      {/* === 02 Ziele & KPIs === */}
      <Section num="02" title="Ziele & KPIs">
        <Question label="Business-Ziel" text="1 Satz: Was wollt ihr damit erreichen?"
          info="KPI = Key Performance Indicator. Das sind messbare Kennzahlen, an denen der Erfolg des Projekts gemessen wird.">
          <input type="text" className="portal-input" value={formData.business_goal || ""}
            onChange={e => updateField("business_goal", e.target.value)}
            placeholder="Wir wollen …" data-testid="field-business_goal" />
        </Question>

        <Question label="Top-3 KPIs inkl. Zielwert"
          info="KPI = Key Performance Indicator. Das sind messbare Kennzahlen, an denen der Erfolg des Projekts gemessen wird.">
          <div className="flex flex-col gap-2 mt-1">
            {[["kpi_1", "KPI 1", "z.B. Conversion Rate ≥ 3 %"],
              ["kpi_2", "KPI 2", "z.B. Cost per Lead ≤ CHF 80"],
              ["kpi_3", "KPI 3", "z.B. 20 qualifizierte Leads / Monat"]].map(([key, label, ph]) => (
              <div key={key} className="grid items-center gap-2" style={{ gridTemplateColumns: "auto 1fr" }}>
                <div className="text-[0.75rem] font-semibold text-[#0F172A] uppercase bg-[#EEF2FF] border-l-[3px] border-[#D4A373] px-3 py-1.5 whitespace-nowrap"
                  style={{ letterSpacing: "0.15em" }}>{label}</div>
                <input type="text" className="portal-input" value={formData[key] || ""}
                  onChange={e => updateField(key, e.target.value)} placeholder={ph} data-testid={`field-${key}`} />
              </div>
            ))}
          </div>
        </Question>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Question label="Wert einer Conversion" text="Ø Deal, LTV oder Abschlussquote"
            info="Was bringt eine einzelne Conversion wirtschaftlich? LTV = Lifetime Value (Kundenwert über die gesamte Geschäftsbeziehung).">
            <input type="text" className="portal-input" value={formData.conversion_value || ""}
              onChange={e => updateField("conversion_value", e.target.value)}
              placeholder="z.B. CHF 15 000 / Projekt, 20 % Close Rate" data-testid="field-conversion_value" />
          </Question>
          <Question label="Bestehende Benchmarks" text="Falls vorhanden: CR, CPL, Traffic/Mt."
            info="Referenzwerte aus Ihrem Analytics oder CRM. CR = Conversion Rate, CPL = Cost per Lead (Kosten pro Anfrage).">
            <input type="text" className="portal-input" value={formData.existing_benchmarks || ""}
              onChange={e => updateField("existing_benchmarks", e.target.value)}
              placeholder="z.B. aktuell 1.2 % CR, 45 Leads/Mt." data-testid="field-existing_benchmarks" />
          </Question>
        </div>
      </Section>

      {/* === 03 Angebot & Value Proposition === */}
      <Section num="03" title="Angebot & Value Proposition">
        <Question label="Was wird angeboten?" text="Produkt, Service oder Paket — klar beschreiben.">
          <textarea className="portal-input portal-textarea tall" value={formData.offering_description || ""}
            onChange={e => updateField("offering_description", e.target.value)}
            placeholder="Beschreibung des Angebots …" data-testid="field-offering_description" />
        </Question>

        <Question label="Top-3 Nutzenargumente" text="Priorisiert — was überzeugt den Kunden wirklich?"
          info="Die 3 wichtigsten Gründe, warum ein Kunde bei Ihnen kaufen sollte — in Reihenfolge der Überzeugungskraft.">
          <div className="flex flex-col gap-2">
            {[["benefit_1", "1", "Wichtigstes Nutzenargument …"],
              ["benefit_2", "2", "Zweites …"],
              ["benefit_3", "3", "Drittes …"]].map(([key, badge, ph]) => (
              <PriorityRow key={key} fieldKey={key} badge={badge} placeholder={ph} formData={formData} updateField={updateField} />
            ))}
          </div>
        </Question>

        <Question label="Differenzierung" text="Warum Sie statt Alternative?">
          <textarea className="portal-input portal-textarea" value={formData.differentiation || ""}
            onChange={e => updateField("differentiation", e.target.value)}
            placeholder="Alleinstellungsmerkmale …" data-testid="field-differentiation" />
        </Question>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Question label="Häufigste Einwände aus Sales">
            <textarea className="portal-input portal-textarea" value={formData.sales_objections || ""}
              onChange={e => updateField("sales_objections", e.target.value)}
              placeholder="z.B. 'zu teuer', 'machen wir intern' …" data-testid="field-sales_objections" />
          </Question>
          <Question label="Risikoreduzierer"
            info="Elemente, die dem Kunden Sicherheit geben und Kaufhemmnisse abbauen — z.B. Garantien, Testphasen, flexible Verträge.">
            <textarea className="portal-input portal-textarea" value={formData.risk_reducers || ""}
              onChange={e => updateField("risk_reducers", e.target.value)}
              placeholder="z.B. kostenloser Discovery Call, monatliche Kündigung …" data-testid="field-risk_reducers" />
          </Question>
        </div>
      </Section>

      {/* === Bonus — Copy Inputs (Dark Section) === */}
      <DarkSection title="Bonus — Copy Inputs">
        <div className="mb-5">
          <span className="text-[0.75rem] font-semibold uppercase text-[#D4A373] mb-1 flex items-center gap-1.5" style={{ letterSpacing: "0.2em" }}>
            Positionierungssatz
            <InfoTip text="Ein prägnanter Satz, der zusammenfasst, wem Sie helfen, welches Problem Sie lösen und was der Kunde davon hat." />
          </span>
          <p className="text-[0.9375rem] font-medium text-[#CBD5E1] mb-2 leading-snug">
            „Wir helfen [Zielgruppe], [Problem] zu lösen, damit [Outcome]."
          </p>
          <input type="text" value={formData.positioning_statement || ""}
            onChange={e => updateField("positioning_statement", e.target.value)}
            placeholder="Wir helfen … zu lösen, damit …"
            className="w-full border-[1.5px] border-[#334155] bg-[#1E293B] text-[#F1F5F9] font-sans text-[0.9375rem] px-3.5 py-2.5 outline-none transition-colors focus:border-[#D4A373]"
            data-testid="field-positioning_statement" />
        </div>
        <div className="mt-5">
          <span className="text-[0.75rem] font-semibold uppercase text-[#D4A373] mb-1 flex items-center gap-1.5" style={{ letterSpacing: "0.2em" }}>
            3 Proof-Statements — Zahlen oder harte Aussagen
            <InfoTip text="Konkrete, belegbare Aussagen, die Vertrauen schaffen — idealerweise mit Zahlen, Ergebnissen oder Auszeichnungen." />
          </span>
          <div className="flex flex-col gap-2 mt-1">
            {[["proof_a", "A", "z.B. '200+ Projekte seit 2018'"],
              ["proof_b", "B", "z.B. 'Ø 40 % schnellere Prozesse nach 90 Tagen'"],
              ["proof_c", "C", "z.B. '4.9/5 Kundenzufriedenheit (n=74)'"]].map(([key, badge, ph]) => (
              <div key={key} className="grid items-center gap-2" style={{ gridTemplateColumns: "auto 1fr" }}>
                <span className="text-[0.6875rem] font-bold uppercase bg-[#0F172A] text-[#D4A373] border border-[#334155] px-2 py-1 text-center min-w-[1.75rem]"
                  style={{ letterSpacing: "0.1em" }}>{badge}</span>
                <input type="text" value={formData[key] || ""}
                  onChange={e => updateField(key, e.target.value)} placeholder={ph}
                  className="w-full border-[1.5px] border-[#334155] bg-[#1E293B] text-[#F1F5F9] font-sans text-[0.9375rem] px-3.5 py-2.5 outline-none transition-colors focus:border-[#D4A373]"
                  data-testid={`field-${key}`} />
              </div>
            ))}
          </div>
        </div>
      </DarkSection>
    </>
  );
}
